import { SceneObject, ConnectionManager, TransformVector } from '@ikea-aoa/component-view-3d';
import { TransformPosition, MeshRenderer, BoxDimensions, Node } from '@ikea-aoa/elements-3d';
import {
	ConnectionRelation,
	Model,
	ModelData,
	ProductData,
} from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import { ObjectManager } from '../ObjectManager';
import { CoverItem, Platform, RangeSettings } from 'sofa-reborn-react';
import { AssetsUrls } from '../../../util/SceneUtils/AssetsUrls';

interface LegConnectionPoint {
	parentId: string;
	relation: ConnectionRelation;
	position: TransformPosition;
}

type Props = {
	rangeSettings: RangeSettings;
	products: ProductData;
	sofaModels: ModelData;
	assetFolder: string | null;
	loadingModels: boolean;
	cover: CoverItem;
	platform: Platform;
	showDebug?: boolean | undefined;
};

export class LegsManager {
	private legsModel: Model | null;
	private assetUrl: string | BoxDimensions;
	public objects = new Array<SceneObject>();

	private connectionManager: ConnectionManager;
	private sceneItems: Map<string, SceneObject>;

	constructor(private objectManager: ObjectManager, private props: Props) {
		this.connectionManager = objectManager.connectionManager;
		this.sceneItems = objectManager.sceneItems;
	}

	/**
	 * Update props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
		this.updateLegsAssetUrl();
	}

	/**
	 * Creates and updates legs objects.
	 *
	 */
	public updateLegs(addAssemblyPoints: boolean): void {
		if (!this.legsModel) {
			return;
		}

		this.removeLegs();

		const filter = {
			valid: false,
			available: true,
			connectionKits: false,
		};

		const relationMap = this.connectionManager.getAllRelations('leg', filter);

		const regularPoints = new Array<LegConnectionPoint>();
		const assemblyPoints = new Array<LegConnectionPoint>();
		const cornerPoints = new Array<LegConnectionPoint>();

		relationMap.forEach((relationsArray, parentId) => {
			const parent = this.sceneItems.get(parentId);
			if (parent) {
				for (let i = 0; i < relationsArray.length; i++) {
					const relation = relationsArray[i];
					const position = this.connectionManager.getPointWorldPosition(relation.parent, parent);
					if (!position) {
						return;
					}
					const isAssembly = relation.parent.toLowerCase().indexOf('assembly') !== -1;
					if (isAssembly) {
						assemblyPoints.push({
							parentId,
							relation,
							position,
						});
						continue;
					}
					const isCorner = relation.parent.toLowerCase().indexOf('corner') !== -1;
					if (isCorner) {
						cornerPoints.push({
							parentId,
							relation,
							position,
						});
						continue;
					}
					regularPoints.push({
						parentId,
						relation,
						position,
					});
				}
			} else {
				throw 'LegsManager: updateObjects(): parnet does not exist';
			}
		});

		// Assembly
		let activeAssemblyPoints = new Array<LegConnectionPoint>();
		for (let i = 0; i < assemblyPoints.length; i++) {
			const pointA = assemblyPoints[i];
			for (let k = i; k < assemblyPoints.length; k++) {
				const pointB = assemblyPoints[k];
				if (pointA === pointB || activeAssemblyPoints.includes(pointB)) {
					continue;
				}
				if (this.inRange(pointA, pointB, 0.2)) {
					activeAssemblyPoints.push(pointB);
				}
			}
		}

		// Corners
		const activeCornerPoints = cornerPoints.filter((pointA) => {
			const inRange = activeAssemblyPoints.filter((pointB) => {
				if (pointA === pointB) {
					return false;
				}
				return this.inRange(pointA, pointB, 1);
			});
			return inRange.length !== 1;
		});

		activeAssemblyPoints = activeAssemblyPoints.filter((pointA) => {
			const inRange = activeCornerPoints.filter((pointB) => {
				if (pointA === pointB) {
					return false;
				}
				return this.inRange(pointA, pointB, 1);
			});

			return inRange.length === 0;
		});

		// Regular
		const activeRegularPoints = regularPoints.filter((pointA) => {
			let inRange = activeCornerPoints.filter((pointB) => {
				if (pointA === pointB) {
					return false;
				}
				return this.inRange(pointA, pointB, 1.2);
			});

			if (addAssemblyPoints) {
				inRange = inRange.concat(
					activeAssemblyPoints.filter((pointB) => {
						if (pointA === pointB) {
							return false;
						}
						return this.inRange(pointA, pointB, 1.2);
					})
				);
			}

			return inRange.length === 0;
		});

		let points = activeCornerPoints.concat(activeRegularPoints);
		if (addAssemblyPoints) {
			points = points.concat(activeAssemblyPoints);
		}

		this.createObjects(points);
	}

	/**
	 * Remove legs objects.
	 *
	 */
	public removeLegs(): void {
		if (this.objects.length) {
			this.objects.forEach((i) => {
				if (this.sceneItems.has(i.id)) {
					this.objectManager.destroyObject(i);
				}
			});
			this.objects = [];
		}
	}

	/**
	 * Sets legs node to be invisible.
	 *
	 * @param node Object node.
	 */
	public hideObjects(node: Node): void {
		const { children } = node;
		const sceneItems = this.sceneItems;
		children.forEach((child) => {
			const obj = sceneItems.get(child.id);
			if (obj && obj.articleId == 'leg') {
				child.visibility = 0;
			}
			if (child.children.size) {
				this.hideObjects(child);
			}
		});
	}

	private inRange(a: LegConnectionPoint, b: LegConnectionPoint, range: number): boolean {
		return TransformVector.distance(a.position, b.position) <= range;
	}

	private createObjects(points: LegConnectionPoint[]): void {
		for (let i = 0; i < points.length; i++) {
			const leg = this.objectManager.createEmptyObject('leg');
			if (!leg || !leg.node) {
				return;
			}
			const meshRenderer = leg.node.addComponent(MeshRenderer, 'MeshRenderer');
			meshRenderer.state.mesh = this.assetUrl;

			this.sceneItems.set(leg.id, leg);
			const parent = this.sceneItems.get(points[i].parentId);
			if (parent && parent.node) {
				this.connectionManager.connect(leg, parent, points[i].relation);
				if (leg.node) {
					leg.node.setParent(parent.node, leg.transform.position, leg.transform.rotation);
				}
				this.objects.push(leg);
			}
		}
	}

	private updateLegsAssetUrl(): void {
		this.legsModel = this.getLegsModel();

		if (!this.legsModel) {
			return;
		}
		const size = this.legsModel.bounds['default'].size;
		const assetUrl = AssetsUrls.createUrlForAssetId(
			this.props.assetFolder,
			this.props.rangeSettings.assets3dSrc,
			this.legsModel.modelId,
			this.props.rangeSettings.modelSwitchMap || null
		);
		const isPreloaded = this.objectManager.isPreloaded(assetUrl);
		if (isPreloaded) {
			this.assetUrl = assetUrl;
		} else {
			this.assetUrl = size;
		}
	}

	private getLegsModel(): Model | null {
		const { products, sofaModels } = this.props;
		if (products['leg'] && sofaModels[products['leg'].modelId]) {
			return sofaModels[products['leg'].modelId];
		}
		return null;
	}
}
